import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GitBranch, Plus } from 'lucide-react';
import {
  deleteInboundRouter,
  listInboundRouters,
} from '../../api/inboundRouters';
import ConfirmModal from '../../components/ui/ConfirmModal';
import EmptyState from '../../components/ui/EmptyState';
import { getApiErrorMessage } from '../../utils/errors';

const PER_PAGE = 20;

function StatusBadge({ status }) {
  const active = status === 'active';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
        active
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : 'bg-slate-100 text-slate-600 border border-slate-200'
      }`}
    >
      {active ? 'Ativo' : 'Inativo'}
    </span>
  );
}

function formatChannelLabel(item) {
  return item.external_channel_id || item.channel_id || '—';
}

function InboundRoutersList() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, per_page: PER_PAGE, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingItem, setDeletingItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadItems = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const response = await listInboundRouters({ page, perPage: PER_PAGE });
      setItems(response.data || []);
      setMeta(response.meta || { page: 1, per_page: PER_PAGE, total: 0 });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar os roteamentos.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleConfirmDelete = async () => {
    if (!deletingItem) {
      return;
    }

    setIsDeleting(true);
    setError('');
    try {
      await deleteInboundRouter(deletingItem.id);
      setDeletingItem(null);
      await loadItems(meta.page);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível remover o roteamento.'));
    } finally {
      setIsDeleting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(meta.total / meta.per_page));
  const hasPagination = meta.total > meta.per_page;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-content-primary">Roteamento inbound</h1>
          <p className="text-content-muted text-sm mt-1">
            Configure como mensagens inbound são distribuídas entre agentes no mesmo WhatsApp.
          </p>
        </div>
        <Link
          to="/inbound-routers/new"
          className="btn-primary text-sm px-4 py-2.5 shrink-0 inline-flex items-center gap-1.5"
        >
          <Plus className="h-4 w-4 shrink-0" aria-hidden />
          Novo roteamento
        </Link>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="surface-card px-4 py-12 text-center">
          <p className="text-content-muted text-sm">Carregando roteamentos...</p>
        </div>
      ) : items.length === 0 && !error ? (
        <EmptyState
          icon={GitBranch}
          title="Nenhum roteamento configurado"
          description="Configure como mensagens inbound são distribuídas entre agentes no mesmo WhatsApp."
          action={(
            <Link
              to="/inbound-routers/new"
              className="btn-primary text-sm px-4 py-2.5 inline-flex items-center gap-1.5"
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              Criar primeiro roteamento
            </Link>
          )}
        />
      ) : (
        <>
          <div className="hidden md:block surface-card overflow-hidden">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase">Canal</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase">Destinos</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-content-muted uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3 text-sm font-medium text-content-primary">{item.name}</td>
                    <td className="px-4 py-3 text-sm text-content-muted font-mono">
                      {formatChannelLabel(item)}
                    </td>
                    <td className="px-4 py-3 text-sm text-content-muted">
                      {(item.agent_routes || []).length}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <Link
                        to={`/inbound-routers/${item.id}/edit`}
                        className="text-sm text-forge-600 hover:text-forge-800"
                      >
                        Editar
                      </Link>
                      <button
                        type="button"
                        onClick={() => setDeletingItem(item)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remover
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {items.map((item) => (
              <div key={item.id} className="surface-card p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-content-primary">{item.name}</p>
                    <p className="text-xs text-content-muted font-mono mt-1">
                      {formatChannelLabel(item)}
                    </p>
                  </div>
                  <StatusBadge status={item.status} />
                </div>
                <p className="text-sm text-content-muted">
                  {(item.agent_routes || []).length} destino
                  {(item.agent_routes || []).length !== 1 ? 's' : ''}
                </p>
                <div className="flex gap-3">
                  <Link
                    to={`/inbound-routers/${item.id}/edit`}
                    className="text-sm text-forge-600 hover:text-forge-800"
                  >
                    Editar
                  </Link>
                  <button
                    type="button"
                    onClick={() => setDeletingItem(item)}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Remover
                  </button>
                </div>
              </div>
            ))}
          </div>

          {hasPagination && (
            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-xs text-content-subtle">
                {meta.total} roteamento{meta.total !== 1 ? 's' : ''} · página {meta.page} de {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={meta.page <= 1}
                  onClick={() => loadItems(meta.page - 1)}
                  className="btn-secondary text-sm px-3 py-2 disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={meta.page >= totalPages}
                  onClick={() => loadItems(meta.page + 1)}
                  className="btn-secondary text-sm px-3 py-2 disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        open={Boolean(deletingItem)}
        title="Remover roteamento"
        message={
          deletingItem
            ? `Remover o roteamento "${deletingItem.name}"? Mensagens inbound deixarão de ser roteadas por esta configuração.`
            : ''
        }
        confirmLabel="Remover"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeletingItem(null)}
        isConfirming={isDeleting}
      />
    </div>
  );
}

export default InboundRoutersList;
